import { createTheme } from "@mui/material/styles";

// Palette follows the LiFE website (tense_life_website): near-black ink on
// warm off-white, with the warm brown accent used sparingly.
export const brand = {
  ink: "#1A1A1A",
  cream: "#FAFAF8",
  sand: "#F5EDE0",
  warm: "#A87340",
  warmSoft: "#E8D5BD",
  border: "#E7E2DC",
  muted: "#6B6B6B",
};

export const serifFont =
  '"Instrument Serif", Georgia, "Times New Roman", serif';
export const monoFont =
  'ui-monospace, "SFMono-Regular", "JetBrains Mono", Menlo, Consolas, monospace';

// Big figures (counts, keys left). Instrument Serif's "1" reads as "l".
export const figureSx = {
  fontWeight: 600,
  letterSpacing: "-0.02em",
  fontVariantNumeric: "tabular-nums",
  lineHeight: 1.1,
};

// Single-series chart bars: a warmer, more saturated step of the brand brown
// (the brand brown itself reads as grey in charts).
export const chartAccent = "#B86A2B";

export const statusColors = {
  available: { fg: "#3F6B4E", bg: "#E5EFE7" },
  issued: { fg: "#7A4E7E", bg: "#F2E8F1" },
  active: { fg: "#2E5A88", bg: "#E2ECF6" },
  expiring: { fg: "#9A5C00", bg: "#FCEFD2" },
  expired: { fg: "#A03A2C", bg: "#FAE4E0" },
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: brand.ink, contrastText: "#FFFFFF" },
    secondary: { main: brand.warm, contrastText: "#FFFFFF" },
    background: { default: brand.cream, paper: "#FFFFFF" },
    text: { primary: brand.ink, secondary: brand.muted },
    divider: brand.border,
    success: { main: "#3F7D58" },
    warning: { main: "#B7791F" },
    error: { main: "#B3412F" },
    info: { main: "#2E5A88" },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
    h3: { fontFamily: serifFont, fontWeight: 400, letterSpacing: "-0.01em" },
    h4: {
      fontFamily: serifFont,
      fontWeight: 400,
      letterSpacing: "-0.01em",
      fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
    },
    h5: { fontFamily: serifFont, fontWeight: 400, fontSize: "1.6rem" },
    h6: { fontWeight: 600, fontSize: "1rem" },
    subtitle2: { fontWeight: 600 },
    overline: { fontWeight: 600, letterSpacing: "0.08em", lineHeight: 2 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: brand.cream } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: "none" } },
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
      styleOverrides: { root: { borderColor: brand.border } },
    },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiFormControl: { defaultProps: { size: "small" } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: brand.border },
        head: {
          fontWeight: 600,
          fontSize: "0.75rem",
          color: brand.muted,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: serifFont, fontSize: "1.6rem", fontWeight: 400 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 3, backgroundColor: brand.sand },
        bar: { borderRadius: 3 },
      },
    },
  },
});

export default theme;
